<template>
  <form
    :class="classes(n())"
    @submit.prevent="handleSubmit"
  >
    <slot />
  </form>
</template>

<script lang="ts">
import { defineComponent, ref, provide, reactive, onMounted, watch, type WatchStopHandle } from 'virtual-vue';
import { props } from './props';
import { formEmits } from './emits';
import { createNamespace } from '../utils/components';
import { FORM_KEY } from './constants';
import type { FieldInstance, FormValidateError } from './types';

const { name, n, classes } = createNamespace('form');

export default defineComponent({
  name,
  props,
  emits: formEmits,
  setup(props, { emit, expose }) {
    const fields = ref<FieldInstance[]>([]);
    const fieldWatchers = new Map<FieldInstance, WatchStopHandle>();
    let mountSnapshot: Record<string, any> = {};

    onMounted(() => {
      if (props.model) {
        mountSnapshot = JSON.parse(JSON.stringify(props.model));
      }
    });

    const getInitialValue = (fieldName: string): any => {
      let val: any;
      if (props.initialValues && fieldName in props.initialValues) {
        val = props.initialValues[fieldName];
      } else if (fieldName in mountSnapshot) {
        val = mountSnapshot[fieldName];
      } else {
        return undefined;
      }
      if (val !== null && typeof val === 'object') {
        return JSON.parse(JSON.stringify(val));
      }
      return val;
    };

    const registerField = (field: FieldInstance) => {
      fields.value.push(field);

      if (props.model && field.name) {
        const stopWatch = watch(
          () => props.model?.[field.name],
          (newVal) => {
            emit('valuesChange', { name: field.name, value: newVal });
          },
        );
        fieldWatchers.set(field, stopWatch);
      }
    };

    const unregisterField = (field: FieldInstance) => {
      const index = fields.value.indexOf(field);
      if (index > -1) {
        fields.value.splice(index, 1);
      }
      const stopWatch = fieldWatchers.get(field);
      if (stopWatch) {
        stopWatch();
        fieldWatchers.delete(field);
      }
    };

    const getFieldsByName = (fieldName?: string | string[]): FieldInstance[] => {
      if (!fieldName) return [...fields.value];
      const names = Array.isArray(fieldName) ? fieldName : [fieldName];
      return fields.value.filter(field => names.includes(field.name));
    };

    const validate = async (fieldName?: string | string[]): Promise<void> => {
      const targetFields = getFieldsByName(fieldName);
      const allErrors: FormValidateError[] = [];

      for (const field of targetFields) {
        const errors = await field.validate();
        if (errors.length > 0) {
          allErrors.push({ name: field.name, errors });
          if (props.validateFirst) break;
        }
      }

      if (allErrors.length > 0) {
        emit('failed', allErrors);
        return Promise.reject(allErrors);
      }
    };

    const validateOnly = async (fieldName?: string | string[]): Promise<void> => {
      const targetFields = getFieldsByName(fieldName);
      const allErrors: FormValidateError[] = [];

      for (const field of targetFields) {
        const errors = await field.validateOnly();
        if (errors.length > 0) {
          allErrors.push({ name: field.name, errors });
          if (props.validateFirst) break;
        }
      }

      if (allErrors.length > 0) {
        return Promise.reject(allErrors);
      }
    };

    const resetFields = (fieldName?: string | string[]) => {
      const targetFields = getFieldsByName(fieldName);
      const { model } = props;

      targetFields.forEach((field) => {
        field.resetValidation();
        if (model && field.name) {
          model[field.name] = getInitialValue(field.name);
        }
      });
    };

    const resetValidation = (fieldName?: string | string[]) => {
      const targetFields = getFieldsByName(fieldName);
      targetFields.forEach(field => field.resetValidation());
    };

    const getValues = (): Record<string, any> => {
      const values: Record<string, any> = {};
      fields.value.forEach((field) => {
        if (props.model && field.name) {
          values[field.name] = props.model[field.name];
        }
      });
      return values;
    };

    const getFieldError = (fieldName: string): string[] => {
      const field = fields.value.find(f => f.name === fieldName);
      return field ? field.getErrors() : [];
    };

    const getFieldsError = (fieldName?: string[]): { name: string; errors: string[] }[] => {
      const targetFields = fieldName
        ? fields.value.filter(f => fieldName.includes(f.name))
        : fields.value;
      return targetFields.map(field => ({
        name: field.name,
        errors: field.getErrors(),
      }));
    };

    const handleSubmit = async () => {
      try {
        await validate();
        emit('submit', getValues());
      } catch {
        // failed 事件已在 validate 内 emit
      }
    };

    // reactive + getter 保持 props 响应性
    provide(FORM_KEY, reactive({
      get model() {
        return props.model;
      },
      get rules() {
        return props.rules;
      },
      get validateTrigger() {
        return props.validateTrigger;
      },
      get validateMessages() {
        return props.validateMessages;
      },
      get showErrorMessage() {
        return props.showErrorMessage;
      },
      get labelWidth() {
        return props.labelWidth;
      },
      get labelAlign() {
        return props.labelAlign;
      },
      get layout() {
        return props.layout;
      },
      get disabled() {
        return props.disabled;
      },
      get readonly() {
        return props.readonly;
      },
      registerField,
      unregisterField,
    }));

    const exposed = {
      validate,
      validateOnly,
      resetFields,
      resetValidation,
      getValues,
      getFieldError,
      getFieldsError,
    };

    if (expose) {
      expose(exposed);
    }

    return {
      n,
      classes,
      handleSubmit,
      ...exposed,
    };
  },
});
</script>
